import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, TrendingDown } from "lucide-react";
import { imgFallback } from "@/lib/compare";

const timeAgo = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export const NotificationBell = ({ dark = true }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState({ notifications: [], unread: 0 });

  const load = useCallback(() => {
    if (!token) return;
    axios
      .get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = (open) => {
    if (open && data.unread > 0) {
      axios.post(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } }).then(() => setData((d) => ({ ...d, unread: 0 })));
    }
  };

  if (!user) return null;
  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${dark ? "bg-white/15 hover:bg-white/25 text-white" : "bg-primary-50 hover:bg-primary-100 text-primary-700"}`} aria-label="Notifications" data-testid="notification-bell-btn">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-secondary-500 text-primary-900 text-[11px] font-extrabold flex items-center justify-center border-2 border-primary-700" data-testid="notification-unread-count">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,360px)] p-0 rounded-3xl overflow-hidden" data-testid="notification-panel">
        <div className="px-4 py-3 bg-primary-700 text-white font-heading font-bold">Price alerts & updates</div>
        <div className="max-h-96 overflow-y-auto divide-y divide-primary-50">
          {data.notifications.length === 0 ? (
            <p className="p-6 text-sm text-primary-400 text-center" data-testid="notification-empty">No alerts yet. Tap the bell on any product to get price-drop alerts.</p>
          ) : (
            data.notifications.map((n) => (
              <Link key={n.id} to={`/product/${n.product_id}`} className={`flex gap-3 p-3 hover:bg-primary-50 ${n.read ? "" : "bg-secondary-50"}`} data-testid={`notification-item-${n.id}`}>
                {n.image_url ? <img src={n.image_url} alt="" onError={imgFallback} className="w-12 h-12 rounded-xl object-cover shrink-0" /> : <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0"><TrendingDown className="w-5 h-5 text-primary-600" /></span>}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-primary-900 flex items-center gap-1"><TrendingDown className="w-4 h-4 text-green-600" />{n.title}</p>
                  <p className="text-xs text-primary-700 line-clamp-2">{n.message}</p>
                  <p className="text-[11px] text-primary-400 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
